import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GracePeriodManager = ({ tenantId, billingStatus, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [gracePeriodDays, setGracePeriodDays] = useState(billingStatus?.payment_grace_period_days || 7);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async () => {
    if (gracePeriodDays < 0 || gracePeriodDays > 30) {
      setError('Grace period must be between 0 and 30 days');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: updateError } = await supabase
        ?.from('tenant_billing_status')
        ?.update({
          payment_grace_period_days: gracePeriodDays,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('tenant_id', tenantId);

      if (updateError) throw updateError;

      setIsEditing(false);
      onUpdate();
    } catch (err) {
      setError(err?.message || 'Failed to update grace period');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <h2 className="text-lg font-heading font-bold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Clock" size={20} color="var(--color-primary)" />
        Grace Period Management
      </h2>

      {error && (
        <div className="mb-4 p-3 bg-destructive/10 border border-destructive rounded-lg flex items-center gap-2">
          <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {!isEditing ? (
        <div>
          <div className="p-4 bg-muted/30 rounded-lg mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Current Grace Period</span>
              <Icon name="Calendar" size={16} color="var(--color-primary)" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              {billingStatus?.payment_grace_period_days || 0}
              <span className="text-lg text-muted-foreground ml-2">days</span>
            </p>
          </div>

          <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg mb-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} color="var(--color-primary)" />
              <div>
                <h4 className="font-semibold text-foreground mb-1">About Grace Period</h4>
                <p className="text-sm text-muted-foreground">
                  The grace period determines how many days after a failed payment the tenant has to resolve the issue before automatic suspension.
                </p>
              </div>
            </div>
          </div>

          <Button
            variant="outline"
            fullWidth
            onClick={() => setIsEditing(true)}
          >
            <Icon name="Edit" size={16} />
            Modify Grace Period
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Grace Period (Days)
            </label>
            <input
              type="number"
              min="0"
              max="30"
              value={gracePeriodDays}
              onChange={(e) => setGracePeriodDays(parseInt(e?.target?.value) || 0)}
              className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-xs text-muted-foreground mt-1">Range: 0-30 days</p>
          </div>

          <div className="p-3 bg-warning/10 border border-warning rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={16} color="var(--color-warning)" />
              <p className="text-xs text-warning">
                Changing the grace period will affect when automatic suspension occurs for overdue payments.
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              fullWidth
              onClick={() => {
                setIsEditing(false);
                setGracePeriodDays(billingStatus?.payment_grace_period_days || 7);
                setError('');
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="default"
              fullWidth
              onClick={handleUpdate}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Icon name="Loader2" size={16} className="animate-spin" />
                  Updating...
                </>
              ) : (
                <>
                  <Icon name="Check" size={16} />
                  Update
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GracePeriodManager;